import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Instagram, Facebook, Send, Loader2 } from "lucide-react";
import { PROFILE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Kusum will get back to you soon.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#111111] border border-neutral-800 focus:border-volt focus:ring-1 focus:ring-volt rounded-none px-4 py-4 w-full text-white placeholder-neutral-500 font-body outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#050505]" data-testid="contact-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-10">
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">05 / Contact</span>
          <span className="h-px flex-1 bg-neutral-800" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-heading uppercase text-[13vw] md:text-[8vw] leading-[0.85] tracking-tighter text-white mb-16"
        >
          Let&apos;s build a<br />
          <span className="text-volt">winning side.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact details */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="relative aspect-[4/3] overflow-hidden border border-neutral-800">
              <img
                src={PROFILE.photos.action}
                alt="Kusum Shaikh at the touchline"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
                data-testid="contact-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
            <a
              href={`mailto:${PROFILE.email}`}
              className="group flex items-center gap-4 border-b border-neutral-800 pb-5 hover:border-volt transition-colors"
              data-testid="contact-email-link"
            >
              <Mail className="text-volt" size={22} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</p>
                <p className="font-body text-white group-hover:text-volt transition-colors">{PROFILE.email}</p>
              </div>
            </a>
            <div className="flex gap-4">
              <a
                href={PROFILE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border border-neutral-800 px-5 py-3 hover:border-volt hover:text-volt transition-colors font-mono text-xs uppercase tracking-wide"
                data-testid="contact-instagram-link"
              >
                <Instagram size={16} /> {PROFILE.social}
              </a>
              <a
                href={PROFILE.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border border-neutral-800 px-5 py-3 hover:border-volt hover:text-volt transition-colors font-mono text-xs uppercase tracking-wide"
                data-testid="contact-facebook-link"
              >
                <Facebook size={16} /> Facebook
              </a>
            </div>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 flex flex-col gap-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your name"
                className={inputCls}
                data-testid="contact-input-name"
              />
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="Email address"
                className={inputCls}
                data-testid="contact-input-email"
              />
            </div>
            <input
              name="subject"
              value={form.subject}
              onChange={onChange}
              placeholder="Subject (club, role, opportunity...)"
              className={inputCls}
              data-testid="contact-input-subject"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              placeholder="Tell me about your project or opportunity..."
              rows={6}
              className={`${inputCls} resize-none`}
              data-testid="contact-input-message"
            />
            <button
              type="submit"
              disabled={loading}
              className="font-heading tracking-wide text-lg bg-volt text-black px-8 py-4 hover:bg-white transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} /> SENDING
                </>
              ) : (
                <>
                  SEND MESSAGE <Send size={18} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
